import { useCallback, useRef, useState } from "react";
import { GridRowId, GridRowParams, GridRowSelectionModel } from "@mui/x-data-grid-premium";

/**
 * Obsługa zaznaczania wierszy w DataGridPremium:
 *  - Ctrl+klik   — przełącza pojedynczy wiersz
 *  - Shift+klik  — zaznacza zakres od kotwicy do klikniętego wiersza
 *  - Ctrl+Shift  — dodaje zakres do istniejącego zaznaczenia
 *  - zwykły klik — czyści zaznaczenie
 *
 * Użycie:
 *   const { selectedRowIds, rowsRef, handleRowClick } = useGridRowSelection();
 *   rowsRef.current = rows;  // po obliczeniu tablicy wierszy
 *   <DataGridPremium
 *     disableRowSelectionOnClick
 *     rowSelectionModel={selectedRowIds}
 *     onRowClick={handleRowClick}
 *   />
 *
 * @param shouldExclude — opcjonalna funkcja wykluczająca wiersze ze selekcji (np. wiersz RAZEM)
 */
const useGridRowSelection = (shouldExclude?: (id: GridRowId) => boolean) => {
  const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>({
    type: "include",
    ids: new Set<GridRowId>(),
  });

  const anchorRowIdRef = useRef<GridRowId | null>(null);
  const rowsRef = useRef<{ id: GridRowId }[]>([]);
  const shouldExcludeRef = useRef(shouldExclude);
  shouldExcludeRef.current = shouldExclude;

  const handleRowClick = useCallback((params: GridRowParams, event: React.MouseEvent) => {
    if (shouldExcludeRef.current?.(params.id)) return;

    if (event.shiftKey && anchorRowIdRef.current !== null) {
      const allIds = rowsRef.current.map((r) => r.id);
      const anchorIdx = allIds.indexOf(anchorRowIdRef.current);
      const currentIdx = allIds.indexOf(params.id);
      if (anchorIdx === -1 || currentIdx === -1) return;
      const from = Math.min(anchorIdx, currentIdx);
      const to = Math.max(anchorIdx, currentIdx);
      const rangeIds = new Set<GridRowId>(allIds.slice(from, to + 1));
      if (event.ctrlKey || event.metaKey) {
        setSelectedRowIds((prev) => {
          const newIds = new Set(prev.ids);
          rangeIds.forEach((id) => newIds.add(id));
          return { type: "include", ids: newIds };
        });
      } else {
        setSelectedRowIds({ type: "include", ids: rangeIds });
      }
    } else if (event.ctrlKey || event.metaKey) {
      anchorRowIdRef.current = params.id;
      setSelectedRowIds((prev) => {
        const newIds = new Set(prev.ids);
        if (newIds.has(params.id)) {
          newIds.delete(params.id);
        } else {
          newIds.add(params.id);
        }
        return { type: "include", ids: newIds };
      });
    } else {
      anchorRowIdRef.current = null;
      setSelectedRowIds((prev) =>
        prev.ids.size > 0 ? { type: "include", ids: new Set<GridRowId>() } : prev,
      );
    }
  }, []);

  return { selectedRowIds, rowsRef, handleRowClick };
};

export default useGridRowSelection;
